import React, { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV_LINKS = [
  { label: "The Amber Story", href: "#story" },
  { label: "Vision & Mission", href: "#vision" },
  { label: "Infrastructure", href: "#infrastructure" },
  { label: "Product Mix", href: "#product-mix" },
  { label: "Certifications", href: "#certifications" },
];

// Amber Living emblem only — text is rendered as HTML for pixel-clean rendering
const EMBLEM_URL = "/amber-emblem.png?v=1";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        backdropFilter: "blur(18px) saturate(1.15)",
        WebkitBackdropFilter: "blur(18px) saturate(1.15)",
        backgroundColor: scrolled
          ? "rgba(255, 251, 244, 0.92)"
          : "rgba(255, 251, 244, 0.75)",
      }}
      data-testid="site-navbar"
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-8 lg:px-10">
        <div
          className={`flex items-center justify-between gap-4 transition-all duration-500 ${
            scrolled ? "h-14 md:h-[60px]" : "h-16 md:h-[76px]"
          }`}
        >
          {/* Brand */}
          <a
            href="#hero"
            className="group inline-flex items-center gap-2.5 shrink-0"
            aria-label="Amber Living — Home"
            data-testid="navbar-brand"
          >
            <img
              src={EMBLEM_URL}
              alt="Amber Living"
              className={`w-auto transition-all duration-500 nav-logo-bob ${
                scrolled ? "h-8 md:h-9" : "h-9 md:h-11"
              }`}
              draggable={false}
              style={{ objectFit: "contain" }}
            />
            <span
              className={`font-semibold tracking-[0.22em] uppercase whitespace-nowrap transition-all duration-500 ${
                scrolled ? "text-[13px] md:text-[14px]" : "text-[14px] md:text-[15.5px]"
              }`}
              style={{
                color: "#3B1930",
                fontFamily: "'Inter', 'Helvetica Neue', system-ui, sans-serif",
              }}
            >
              Amber Living
            </span>
          </a>

          {/* Desktop nav */}
          <nav
            className="hidden md:flex items-center flex-1 justify-center gap-1 lg:gap-2"
            onMouseLeave={() => setActiveIdx(-1)}
          >
            {NAV_LINKS.map((link, i) => (
              <a
                key={link.label}
                href={link.href}
                onMouseEnter={() => setActiveIdx(i)}
                className={`relative px-3 lg:px-4 py-2 text-[13px] lg:text-[13.5px] font-medium transition-colors duration-200 whitespace-nowrap nav-link-bob d${i}`}
                style={{
                  color:
                    activeIdx === i ? "#B36478" : "rgba(59,25,48,0.72)",
                }}
                data-testid={`nav-link-${link.label
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
              >
                {link.label}
                <span
                  className="absolute left-1/2 -translate-x-1/2 bottom-0.5 rounded-full transition-all duration-500"
                  style={{
                    width: activeIdx === i ? 22 : 0,
                    height: 2,
                    background:
                      "linear-gradient(90deg, #B36478 0%, #7A3A52 100%)",
                    boxShadow:
                      activeIdx === i ? "0 0 8px rgba(179,100,120,0.6)" : "none",
                  }}
                />
              </a>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <a
              href="#contact"
              className="group relative inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-[13px] font-semibold overflow-hidden transition-all duration-300 hover:-translate-y-0.5 nav-cta-glow"
              style={{
                color: "#3B1930",
                border: "1.5px solid rgba(59,25,48,0.4)",
                backgroundColor: "transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#3B1930";
                e.currentTarget.style.color = "#FBE9DA";
                e.currentTarget.style.borderColor = "#3B1930";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#3B1930";
                e.currentTarget.style.borderColor = "rgba(59,25,48,0.4)";
              }}
              data-testid="navbar-contact-btn"
            >
              <span className="relative z-10">Contact Us</span>
              <ArrowUpRight
                className="w-3.5 h-3.5 relative z-10 transition-transform duration-300 group-hover:rotate-45"
                strokeWidth={2.5}
              />
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md transition-colors"
            style={{ color: "#3B1930" }}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="navbar-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            mobileOpen ? "max-h-[520px] pb-4" : "max-h-0"
          }`}
        >
          <nav className="flex flex-col gap-1 pt-2">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 rounded-md text-[15px] font-medium transition-colors"
                style={{ color: "#3B1930" }}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-[15px] font-semibold"
              style={{
                color: "#FBE9DA",
                backgroundColor: "#3B1930",
              }}
            >
              Contact Us <ArrowUpRight className="w-4 h-4" />
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
